import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAppStore from '../../store';
import { loginRequest, requestOtp, verifyOtp } from '../../lib/api/auth';
import {
  AuthError,
  AuthField,
  AuthShell,
  AuthSubmit
} from './components/AuthForm';
import OAuthButtons from './components/OAuthButtons';
import { destinationForSession } from './postLogin';

/**
 * Sign-in (T8). Two modes:
 *  - password: LIVE against PocketBase /api/collections/users/*
 *  - email OTP: LIVE against PocketBase /api/collections/users/* (§3 hardening:
 *    a failed verify clears the otpId so a dead code can never be retried — a
 *    fresh request issues a fresh otpId, and the 60s cooldown keeps request
 *    spam down; "Resend code" re-issues a code to the same address).
 * A password reset completed via the email link lands back here with
 * state:{reset:true} and shows the confirmation notice (auth-full-spec §2).
 */
function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const applySession = useAppStore((state) => state.applySession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpId, setOtpId] = useState('');
  const [mode, setMode] = useState('password'); // 'password' | 'otp-request' | 'otp-verify'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownActive = cooldown > 0;

  // 60s cooldown on (re)requesting an OTP code (§3) — gentle, not blocking:
  // only the request/resend action is disabled, verification stays possible.
  useEffect(() => {
    if (!cooldownActive) return undefined;
    const timer = window.setInterval(() => {
      setCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownActive]);

  // Password reset just completed (navigated from /reset-password, §2).
  useEffect(() => {
    if (location.state?.reset) {
      setNotice('Password updated — sign in with your new password.');
    }
  }, [location.state]);

  const finishLogin = (result) => {
    applySession(result);
    navigate(destinationForSession(result.session, location), { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (mode === 'otp-request') {
        const result = await requestOtp({ email });
        setOtpId(result.otpId);
        setMode('otp-verify');
        setCooldown(60);
        setNotice(`Code sent to ${email}. It expires in 5 minutes.`);
      } else if (mode === 'otp-verify') {
        const result = await verifyOtp({ otpId, otp });
        finishLogin(result);
      } else {
        const result = await loginRequest({ email, password });
        finishLogin(result);
      }
    } catch (requestError) {
      // §3 hardening: a dead/invalid code must not sit in state — clear the
      // otpId so the stale code can never be retried; the user requests a
      // fresh one (new request ⇒ new otpId ⇒ latest code is the one that works).
      setOtpId('');
      if (mode === 'otp-verify') setCooldown(0);
      setError(requestError.message || 'Unable to sign in right now.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await requestOtp({ email });
      setOtpId(result.otpId);
      setCooldown(60);
      setNotice(`New code sent to ${email}. The previous code is no longer valid.`);
    } catch (requestError) {
      setError(requestError.message || "Couldn't send code — try again.");
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
    if (next !== 'otp-verify') {
      setOtpId('');
      setOtp('');
      setCooldown(0);
    }
  };

  return (
    <AuthShell
      eyebrow="OPERATOR ACCESS"
      subtitle="Sign in to run the agency, or review your tenant portal."
      title={mode === 'otp-verify' ? 'Enter your code' : 'Sign in'}
    >
      <form onSubmit={handleSubmit}>
        {mode !== 'otp-verify' && (
          <AuthField
            autoComplete="email"
            id="login-email"
            label="Work email"
            onChange={setEmail}
            placeholder="you@company.com"
            type="email"
            value={email}
          />
        )}

        {mode === 'password' && (
          <AuthField
            autoComplete="current-password"
            id="login-password"
            label="Password"
            onChange={setPassword}
            placeholder="••••••••"
            type="password"
            value={password}
          />
        )}

        {mode === 'otp-verify' && (
          <AuthField
            autoComplete="one-time-code"
            hint="8-digit code from your email"
            id="login-otp"
            label="One-time code"
            onChange={setOtp}
            placeholder="12345678"
            type="text"
            inputMode="numeric"
            value={otp}
          />
        )}

        {notice && (
          <div className="auth-notice" role="status">{notice}</div>
        )}
        <AuthError message={error} />
        <AuthSubmit busy={busy}>
          {mode === 'otp-request' ? 'Email me a code'
            : mode === 'otp-verify' ? 'Verify code'
              : 'Sign in'}
        </AuthSubmit>
      </form>

      <div className="auth-links">
        {mode === 'password' && (
          <button className="auth-link-button" onClick={() => switchMode('otp-request')} type="button">
            Sign in with an email code
          </button>
        )}
        {mode === 'otp-request' && (
          <button className="auth-link-button" onClick={() => switchMode('password')} type="button">
            Use password instead
          </button>
        )}
        {mode === 'otp-verify' && (
          <>
            <button
              className="auth-link-button"
              disabled={busy || cooldownActive}
              onClick={handleResend}
              type="button"
            >
              {cooldownActive ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
            <button className="auth-link-button" onClick={() => switchMode('otp-request')} type="button">
              Use a different email
            </button>
          </>
        )}
        {mode === 'password' && <Link to="/forgot-password">Forgot password?</Link>}
        {mode === 'password' && <Link to="/register">Create an account</Link>}
      </div>

      {mode === 'password' && <OAuthButtons />}

      {mode === 'password' && (
        <div className="auth-demo">
          <small>Password sign-in is live — accounts are provisioned by the agency.</small>
        </div>
      )}
    </AuthShell>
  );
}

export default LoginPage;
